import { useMemo } from 'react';
import type { ApiResponse } from 'vopay';
import type { RolePayload, UsersHostProps } from 'shared/users';
import { api } from '@/services/api.config';
import { usePartnerData } from '@/providers/PartnerData.provider';
import { propagateRoleDeleteToSubAccounts } from './roleSubAccounts/propagateRoleDeleteToSubAccounts';
import { propagateRoleToSubAccounts } from './roleSubAccounts/propagateRoleToSubAccounts';
import { propagateRoleUpdateToSubAccounts } from './roleSubAccounts/propagateRoleUpdateToSubAccounts';

type UseAccountUsersHostPropsOptions = {
  /** The viewed account's own JWT; scopes every call the feature makes to that account. */
  bearer: string;
  /** Already translated: the heading differs per portal and shared has no portal copy. */
  title: string;
  /** The account has (or may create) sub-accounts, so a role can be applied to every one of them. */
  hasSubAccounts: boolean;
};

// The shared feature expects every call to reject on failure, `Success: false` included. The api layer
// resolves those, so this turns them into rejections without logging - the fan-out helpers log themselves.
async function ensureSuccess<T extends ApiResponse>(request: Promise<T>): Promise<T> {
  const res = await request;

  if (!res.Success) throw new Error(res.ErrorMessage);

  return res;
}

// `ensureSuccess` that also logs once, as the Direct Pattern hooks do.
async function unwrap<T extends ApiResponse>(request: Promise<T>): Promise<T> {
  try {
    return await ensureSuccess(request);
  } catch (error) {
    console.error(error);
    throw error;
  }
}

// The role endpoints take the permission keys as one comma-separated string.
function toRoleRequest(role: RolePayload) {
  return { ...role, Permissions: role.Permissions.join(',') };
}

/**
 * The one place partner-dashboard maps its own model onto what the shared Users feature takes as
 * props (ID-13429), the way client-dashboard's useUsersHostProps does for the account portal. The
 * partner's single view-only boolean collapses into the feature's per-action flags, and the viewed
 * account's bearer stands in for the account portal's session everywhere - the sub-account fan-out
 * included, which lists the account's sub-accounts as the account and then acts as each of them.
 */
export default function useAccountUsersHostProps({
  bearer,
  title,
  hasSubAccounts,
}: UseAccountUsersHostPropsOptions): UsersHostProps {
  const {
    response: { loginUsername, isRoleViewOnly },
  } = usePartnerData();

  // Role fan-out across sub-accounts (create/update/delete/count); undefined when the account has none.
  const subAccountFns = useMemo<Pick<
    UsersHostProps,
    'createRoleInSubAccounts' | 'updateRoleInSubAccounts' | 'countSubAccounts' | 'deleteRoleFromSubAccounts'
  > | null>(() => {
    if (!hasSubAccounts) return null;

    const fetchSubAccounts = (params: Parameters<typeof api.fetchSubAccounts>[0]) =>
      api.fetchSubAccounts(params, undefined, bearer);

    const subAccountRoleDeps = {
      fetchSubAccountsAsync: fetchSubAccounts,
      fetchAccountUserRolesWithBearer: (childBearer: string) => api.fetchAccountUserRoles(childBearer),
    };

    return {
      countSubAccounts: async () =>
        (await unwrap(fetchSubAccounts({ ShowToken: true, ActiveOnly: true }))).Subaccounts.length,
      deleteRoleFromSubAccounts: (roleName) =>
        propagateRoleDeleteToSubAccounts(roleName, {
          ...subAccountRoleDeps,
          deleteUserRole: (params, childBearer) =>
            ensureSuccess(api.deleteAccountUserRole(params, undefined, childBearer)),
        }),
      createRoleInSubAccounts: (role) =>
        propagateRoleToSubAccounts(toRoleRequest(role), {
          fetchSubAccountsAsync: fetchSubAccounts,
          createUserRole: (params, childBearer) =>
            ensureSuccess(api.postCreateAccountUserRole(params, undefined, childBearer)),
        }),
      updateRoleInSubAccounts: (role, options) =>
        propagateRoleUpdateToSubAccounts(
          toRoleRequest(role),
          {
            ...subAccountRoleDeps,
            updateUserRole: (params, childBearer) =>
              ensureSuccess(api.postUpdateAccountUserRole(params, undefined, childBearer)),
          },
          options,
        ),
    };
  }, [bearer, hasSubAccounts]);

  return useMemo(
    () => ({
      title,
      // Spec 5.6.4: the tab's heading, not a page title; the tab card is the container.
      titleLevel: '3',
      titlePlacement: 'inline',
      tableContainer: 'bare',
      // A partner user is not a user of the viewed account, so the role catalog is read as the account.
      permissionCatalogSource: 'account',
      // The partner portal keeps no timezone preference, and the tab this replaces sent none.
      viewer: { username: loginUsername, timezone: 'UTC' },
      account: { bearer, hasSubAccounts },
      userPermissions: {
        canAddUsers: !isRoleViewOnly,
        canManageUserPermissions: !isRoleViewOnly,
        canViewRoles: true,
        canManageRoles: !isRoleViewOnly,
      },
      ...subAccountFns,
    }),
    [title, bearer, hasSubAccounts, loginUsername, isRoleViewOnly, subAccountFns],
  );
}
